import { useRef, useState, type ReactElement, type RefObject } from 'react';
import { Button, Modal, Radio, Select } from 'antd';
import { ARRANGEMENT_LIST_DIVISIONS_MAX, ARRANGEMENT_TYPES, type ArrangementType } from '@pams/shared';
import { useDivisionOptions } from '@/hooks/useDivisionOptions';
import {
  activeFilterCount,
  canonicalListFilters,
  sameListFilters,
  type ArrangementListFilters,
} from '@/lib/arrangementListFilters';
import { withChosenOptions, type SelectOption } from '@/lib/selectOptions';
import { LIST_FILTERS_COPY } from '../copy';
import { bookletChoices, ORDER_TYPE_CHOICES } from '../listFilters';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LIST-FILTERS] THE "ADJUST FILTERS" DIALOG — A DRAFT, APPLIED ONLY ON "APPLY FILTERS".
 *
 *  ⚠️ ITS OWN FILE, AND THE ONLY NEW OVERLAY SITE. `pages/Arrangements/index.tsx` is EXEMPT in
 *  `e2e/fixtures/overlays.ts` at an exact Modal, Tooltip and Dropdown count, and a fourth `<Modal`
 *  declared there would break that arithmetic for an overlay trivially sweepable from here — the
 *  `UploadOrderModal` precedent, word for word. ONE census site: `ArrangementFiltersModal.tsx#Modal@0`.
 *
 *  ⚠️ THE DRAFT LIVES IN THE BODY, AND THE BODY IS KEYED ON EACH OPENING. `destroyOnHidden` unmounts
 *  the children only once the LEAVE MOTION ends — so a reopen during it, in a browser, and every reopen
 *  in jsdom, which never fires the `transitionend` that ends it (`DecisionModal.test.tsx` measured it),
 *  would otherwise show the draft somebody abandoned. A fresh key per opening resets it on every path —
 *  Cancel, Escape, the ×, a mask click — which is [LIFECYCLE]'s "the reset belongs to the dismissal"
 *  without a reset in four handlers. `destroyOnHidden` stays, so a closed dialog mounts nothing and
 *  asks for nothing.
 *
 *  ⚠️ FOCUS GOES TO THE FIRST FIELD FROM `afterOpenChange(true)`, NEVER `autoFocus`. React focuses an
 *  `autoFocus` field during commit, before rc-dialog's effect records which element opened the dialog
 *  — so focus would not return to the button on close. `afterOpenChange` runs after rc-dialog has
 *  focused its own content and recorded the opener.
 *
 *  ⚠️ NO `<form>`. Implicit submission on Enter would compete with the Select's own Enter, which picks
 *  the active option. There is no dialog-level Enter-to-apply: Enter on a footer button presses it.
 *
 *  ⚠️ SECTIONS ARE A MODULE-LEVEL REGISTRY, `FILTER_SECTIONS`, AND THAT IS THE EXTENSION POINT. A new
 *  filter is a section here plus the three other steps `listFilters.ts` lists; a module-level component
 *  is also what the react-hooks `static-components` rule asks of anything rendered from a list.
 *
 *  ⚠️ [TYPE-FILTER] AND EACH SECTION SAYS WHERE IT IS OFFERED. Order type is Manage Arrangements' alone —
 *  a typed list IS its type — and Division is offered where a division can be chosen or one is applied
 *  (the old button gate, moved here now that the dialog opens for Order type on its own). Division stays
 *  FIRST, so it keeps the focus and the browser suite's flow; where it is not offered, Order type is first
 *  and its checked radio takes the focus.
 *
 *  ⚠️ [DP2026] BOOKLET IS THE TYPED LIST'S ALONE, the mirror of Order type: which template set made the
 *  orders (All · the type's own year · Earlier). It follows Division, so Division keeps the focus where it
 *  is offered; on a typed list where it is not, Booklet is first and its checked radio takes the focus.
 *
 *  ⚠️ A FIELD THE DIALOG DOES NOT SHOW IS NEVER WRITTEN FROM ITS DRAFT. The draft is a copy taken when the
 *  dialog opens, and `applied` stays live — another tab can move it while the dialog is open. So the
 *  sections are decided ONCE per opening (none appears or vanishes under the operator), each section TAKES
 *  only its own field from the draft, and every other field is the live applied value. Without it, Apply on
 *  Durga Puja PA wrote back an Order type another tab had just cleared, and a unit seat's Apply on Manage
 *  Arrangements cleared a division it could not see.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** All a section's first field has to offer the dialog: a way to take the focus. */
type FocusHandle = { readonly focus: () => void };

type FilterSectionProps = {
  readonly draft: ArrangementListFilters;
  readonly onChange: (next: ArrangementListFilters) => void;
  /** The FIRST offered section's first field takes focus when the dialog opens; every other gets `undefined`. */
  readonly focusRef: RefObject<FocusHandle | null> | undefined;
  /** [DP2026] The typed list's own type, or `undefined` on Manage Arrangements — Booklet names its year. */
  readonly fixedType: ArrangementType | undefined;
};

const DIVISION_FIELD_ID = 'list-filters-division';
const DIVISION_HELP_ID = 'list-filters-division-help';

/**
 * ⚠️ THE CHOSEN IDS ARE ALWAYS GIVEN A LABEL, AND WHICH LABEL DEPENDS ON WHETHER THE TREE HAS ANSWERED.
 *
 * rc-select paints a value with no option as the RAW VALUE — an ObjectId (`lib/selectOptions.ts`). So:
 *  - LOADED: `withChosenOptions` over the FLAT live list, keeping only what it appended — a chosen id
 *    the live divisions do not hold, named if the tree can name it, else "No longer available".
 *    ⚠️ Never run over an empty list while loading: every applied id would read "No longer available",
 *    which is false until the tree has answered.
 *  - LOADING: "Loading…". FAILED: "Not loaded" — a tree that did not load says nothing about whether a
 *    division exists.
 *
 * ⚠️ GROUPED "Territorial divisions" / "Wings" ([WINGS.1]'s two bands), an empty group omitted, and the
 * fallbacks appended UNGROUPED at the end — `withChosenOptions` is flat-typed, so the missing ids are
 * worked out on the flat list and only then placed beside the groups. A search matching a group's
 * label keeps the whole group, which is rc-select's own behaviour.
 *
 * ⚠️ NO `maxTagCount="responsive"`: it measures widths, jsdom reports zero, and every chip collapses into
 * a hidden overflow item ([C2b §5e]). The chips wrap. `maxCount` is the server's own bound, so nothing
 * the picker allows can be refused for its length.
 */
const DivisionFilterSection = ({ draft, onChange, focusRef }: FilterSectionProps): ReactElement => {
  const { options, resolve, isPending, isError, isLoaded } = useDivisionOptions({ enabled: true });
  const chosen = draft.divisionOrgUnitIds;

  const live = (territorial: boolean): SelectOption[] =>
    options.filter((option) => option.territorial === territorial).map(({ value, label }) => ({ value, label }));
  const territorial = live(true);
  const wings = live(false);
  const flat = [...territorial, ...wings];
  const fallbacks: SelectOption[] = isLoaded
    ? withChosenOptions(flat, chosen, resolve).slice(flat.length)
    : chosen.map((id) => ({
        value: id,
        label: isError ? LIST_FILTERS_COPY.chipFailed : LIST_FILTERS_COPY.chipLoading,
      }));

  return (
    <section data-filter-section="division" className="flex flex-col gap-1">
      <label htmlFor={DIVISION_FIELD_ID} className="text-sm font-medium">
        {LIST_FILTERS_COPY.division}
      </label>
      <Select
        ref={(select) => {
          if (focusRef) focusRef.current = select;
        }}
        id={DIVISION_FIELD_ID}
        mode="multiple"
        showSearch
        optionFilterProp="label"
        allowClear
        className="w-full"
        placeholder={LIST_FILTERS_COPY.divisionPlaceholder}
        maxCount={ARRANGEMENT_LIST_DIVISIONS_MAX}
        loading={isPending}
        disabled={isError}
        aria-describedby={DIVISION_HELP_ID}
        data-testid="list-filters-division"
        notFoundContent={isPending ? LIST_FILTERS_COPY.loading : LIST_FILTERS_COPY.none}
        value={[...chosen]}
        onChange={(values: string[]) => {
          onChange({ ...draft, divisionOrgUnitIds: values });
        }}
        options={[
          ...(territorial.length > 0 ? [{ label: LIST_FILTERS_COPY.territorial, options: territorial }] : []),
          ...(wings.length > 0 ? [{ label: LIST_FILTERS_COPY.wings, options: wings }] : []),
          ...fallbacks,
        ]}
      />
      <p id={DIVISION_HELP_ID} className="text-xs text-muted-foreground">
        {LIST_FILTERS_COPY.divisionHelp}
      </p>
      {isError && <p className="text-xs text-destructive">{LIST_FILTERS_COPY.failed}</p>}
    </section>
  );
};

const TYPE_LABEL_ID = 'list-filters-type-label';
const TYPE_HELP_ID = 'list-filters-type-help';
/**
 * ⚠️ ITS OWN NAME. The native radio set the arrow keys walk is every input sharing a `name`, and AntD's
 * default comes from rc-util's `useId` — the constant `test-id` under jsdom ([P7C.1]), so every group on
 * the page would be one set there. A fixed name makes it exactly these choices everywhere.
 */
const TYPE_GROUP_NAME = 'list-filters-type';

/**
 * [TYPE-FILTER] "Order type" — All, Regular PA only, one choice per registered type
 * (`ORDER_TYPE_CHOICES`). A labelled RADIOGROUP on a wrapper, because AntD's group adds no role or name of
 * its own and its props do not declare `role`; a native radio set, so the arrow keys move the choice. Its
 * focus handle focuses the CHECKED radio — a `div` cannot take the focus. No Tooltip: the census is unchanged.
 *
 * ⚠️ EXPORTED FOR ITS FOCUS HANDLE'S TEST ALONE: jsdom never ends the Modal's appear motion, so
 * `afterOpenChange` never runs there and the page cannot show where the focus lands.
 */
export const TypeFilterSection = ({ draft, onChange, focusRef }: FilterSectionProps): ReactElement => (
  <section data-filter-section="type" className="flex flex-col gap-1">
    <span id={TYPE_LABEL_ID} className="text-sm font-medium">
      {LIST_FILTERS_COPY.type}
    </span>
    <div
      ref={(group) => {
        if (focusRef) {
          focusRef.current = group && {
            focus: () => {
              group.querySelector<HTMLInputElement>('input:checked')?.focus();
            },
          };
        }
      }}
      role="radiogroup"
      aria-labelledby={TYPE_LABEL_ID}
      aria-describedby={TYPE_HELP_ID}
      data-testid="list-filters-type"
    >
      <Radio.Group
        name={TYPE_GROUP_NAME}
        className="flex flex-col gap-1"
        value={draft.type}
        onChange={(event) => {
          const chosen = ORDER_TYPE_CHOICES.find((choice) => choice.value === event.target.value);
          if (chosen) onChange({ ...draft, type: chosen.value });
        }}
      >
        {ORDER_TYPE_CHOICES.map((choice) => (
          <Radio key={choice.value} value={choice.value}>
            {choice.label}
          </Radio>
        ))}
      </Radio.Group>
    </div>
    <p id={TYPE_HELP_ID} className="text-xs text-muted-foreground">
      {LIST_FILTERS_COPY.typeHelp}
    </p>
  </section>
);

const BOOKLET_LABEL_ID = 'list-filters-booklet-label';
const BOOKLET_HELP_ID = 'list-filters-booklet-help';
/** ⚠️ Its own name, for `TYPE_GROUP_NAME`'s reason — never two radio groups sharing one set. */
const BOOKLET_GROUP_NAME = 'list-filters-booklet';

/**
 * [DP2026] "Booklet" — the Durga Puja PA list's: All booklets, the type's own year, or Earlier booklets.
 * `TypeFilterSection`'s shape exactly — a labelled radiogroup on a wrapper, a native radio set, a focus
 * handle on the checked radio, no Tooltip. Offered only where `fixedType` is set, so the guard below is a
 * belt for the type checker rather than a branch anybody reaches.
 */
export const BookletFilterSection = ({ draft, onChange, focusRef, fixedType }: FilterSectionProps): ReactElement => {
  if (fixedType === undefined) return <></>;
  const choices = bookletChoices(fixedType);
  return (
    <section data-filter-section="booklet" className="flex flex-col gap-1">
      <span id={BOOKLET_LABEL_ID} className="text-sm font-medium">
        {LIST_FILTERS_COPY.booklet}
      </span>
      <div
        ref={(group) => {
          if (focusRef) {
            focusRef.current = group && {
              focus: () => {
                group.querySelector<HTMLInputElement>('input:checked')?.focus();
              },
            };
          }
        }}
        role="radiogroup"
        aria-labelledby={BOOKLET_LABEL_ID}
        aria-describedby={BOOKLET_HELP_ID}
        data-testid="list-filters-booklet"
      >
        <Radio.Group
          name={BOOKLET_GROUP_NAME}
          className="flex flex-col gap-1"
          value={draft.booklet}
          onChange={(event) => {
            const chosen = choices.find((choice) => choice.value === event.target.value);
            if (chosen) onChange({ ...draft, booklet: chosen.value });
          }}
        >
          {choices.map((choice) => (
            <Radio key={choice.value} value={choice.value}>
              {choice.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
      <p id={BOOKLET_HELP_ID} className="text-xs text-muted-foreground">
        {LIST_FILTERS_COPY.bookletHelp(ARRANGEMENT_TYPES[fixedType].year)}
      </p>
    </section>
  );
};

/** Which list the dialog serves, and whether its caller may choose a division at all. */
type SectionContext = {
  readonly fixedType: ArrangementType | undefined;
  readonly divisionOffered: boolean;
  readonly applied: ArrangementListFilters;
};

type FilterSection = {
  readonly key: string;
  readonly Section: (props: FilterSectionProps) => ReactElement;
  readonly offered: (context: SectionContext) => boolean;
  /** The section's OWN field, copied from the draft onto `base` — the only field it may write. */
  readonly take: (base: ArrangementListFilters, draft: ArrangementListFilters) => ArrangementListFilters;
};

const FILTER_SECTIONS: readonly FilterSection[] = [
  {
    key: 'division',
    Section: DivisionFilterSection,
    /* Where a division can be chosen — or one is applied, so whoever holds one can always change it. */
    offered: ({ divisionOffered, applied }) => divisionOffered || applied.divisionOrgUnitIds.length > 0,
    take: (base, draft) => ({ ...base, divisionOrgUnitIds: draft.divisionOrgUnitIds }),
  },
  {
    key: 'type',
    Section: TypeFilterSection,
    offered: ({ fixedType }) => fixedType === undefined,
    take: (base, draft) => ({ ...base, type: draft.type }),
  },
  {
    key: 'booklet',
    Section: BookletFilterSection,
    offered: ({ fixedType }) => fixedType !== undefined,
    take: (base, draft) => ({ ...base, booklet: draft.booklet }),
  },
];

type ArrangementFiltersModalProps = {
  open: boolean;
  /** What the store holds now — canonical, so "unchanged" is a comparison and not a guess. */
  applied: ArrangementListFilters;
  /** [TYPE-FILTER] The typed list's own type, or `undefined` on Manage Arrangements. */
  fixedType: ArrangementType | undefined;
  /** Whether the caller may choose a division (`mayFilterByDivision`). */
  divisionOffered: boolean;
  /** Called with the CANONICAL draft, only when it differs from `applied`. An emptied draft clears. */
  onApply: (next: ArrangementListFilters) => void;
  onClear: () => void;
  onClose: () => void;
};

/**
 * The draft and the footer. ⚠️ CLEAR ACTS ON WHAT IS APPLIED, NOT ON THE DRAFT — it is disabled with
 * nothing applied however the chips stand, and it clears at once and closes; emptying the chips and
 * pressing Apply is the same outcome, by the draft. Apply is disabled while the canonical draft says
 * what is already applied, so a press always changes something.
 */
const FiltersBody = ({
  applied,
  fixedType,
  divisionOffered,
  onApply,
  onClear,
  onClose,
  focusRef,
}: Omit<ArrangementFiltersModalProps, 'open'> & { focusRef: RefObject<FocusHandle | null> }): ReactElement => {
  const [draft, setDraft] = useState<ArrangementListFilters>(applied);
  /* Once per opening — the body is keyed per opening — so no section appears or vanishes mid-edit. */
  const [sections] = useState(() =>
    FILTER_SECTIONS.filter(({ offered }) => offered({ fixedType, divisionOffered, applied })),
  );
  /* What Apply writes: the live applied value, with ONLY the shown sections' fields taken from the draft. */
  let shown = applied;
  for (const { take } of sections) shown = take(shown, draft);
  const next = canonicalListFilters(shown);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">{LIST_FILTERS_COPY.intro}</p>
      {sections.map(({ key, Section }, index) => (
        <Section
          key={key}
          draft={draft}
          onChange={setDraft}
          focusRef={index === 0 ? focusRef : undefined}
          fixedType={fixedType}
        />
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          data-testid="list-filters-clear"
          disabled={activeFilterCount(applied, fixedType) === 0}
          onClick={onClear}
        >
          {LIST_FILTERS_COPY.clear}
        </Button>
        <div className="flex items-center gap-2">
          <Button data-testid="list-filters-cancel" onClick={onClose}>
            {LIST_FILTERS_COPY.cancel}
          </Button>
          <Button
            type="primary"
            data-testid="list-filters-apply"
            disabled={sameListFilters(next, applied)}
            onClick={() => {
              onApply(next);
            }}
          >
            {LIST_FILTERS_COPY.apply}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ArrangementFiltersModal = ({
  open,
  applied,
  fixedType,
  divisionOffered,
  onApply,
  onClear,
  onClose,
}: ArrangementFiltersModalProps) => {
  const firstField = useRef<FocusHandle>(null);
  /*
   * One key per OPENING, counted by adjusting state during render (React's documented pattern, the
   * page's own `discardContextSeen` idiom) — never in an effect, which would paint the old draft first.
   */
  const [wasOpen, setWasOpen] = useState(open);
  const [opening, setOpening] = useState(0);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpening((count) => count + 1);
  }

  return (
    <Modal
      open={open}
      title={LIST_FILTERS_COPY.modalTitle}
      footer={null}
      destroyOnHidden
      data-testid="list-filters-modal"
      onCancel={onClose}
      afterOpenChange={(isOpen) => {
        if (isOpen) firstField.current?.focus();
      }}
    >
      <FiltersBody
        key={opening}
        applied={applied}
        fixedType={fixedType}
        divisionOffered={divisionOffered}
        onApply={onApply}
        onClear={onClear}
        onClose={onClose}
        focusRef={firstField}
      />
    </Modal>
  );
};
